import { useEffect, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Building2,
  Loader2,
  Mail,
  Plus,
  Sparkles,
} from 'lucide-react'
import { apiFetch, apiJson, ApiError } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/lib/auth/useAuth'
import { PageMain } from '@/components/page-main'

interface Organization {
  id: string
  name: string
  display_name: string | null
  created_at: string
}

export const Route = createFileRoute('/onboarding')({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isLoading && !context.auth.isAuthenticated) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    // Already have an active org -> nothing to onboard. Use currentOrgSlug
    // (not currentOrg) so platform admins picking a non-member org are
    // recognised as having an active context.
    if (context.auth.isAuthenticated && context.auth.currentOrgSlug) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: OnboardingPage,
})

function OnboardingPage() {
  const { user, isPlatformAdmin, currentOrgSlug } = useAuth()
  const navigate = useNavigate()

  // Reactive escape hatch: once the session refresh propagates an active org
  // (after invite accept, switch-org, or org create), bail out of onboarding.
  // beforeLoad only runs on navigations, so this useEffect is what catches
  // the post-mutation state update.
  useEffect(() => {
    if (currentOrgSlug) {
      navigate({ to: '/dashboard' })
    }
  }, [currentOrgSlug, navigate])

  return (
    <PageMain className="relative min-h-screen flex items-center justify-center overflow-hidden p-6">
      <div className="absolute inset-0 hawk-grid pointer-events-none" aria-hidden />
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(255, 77, 0, 0.08), transparent 70%)',
        }}
      />

      <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground flex items-center gap-4">
        <span className="text-foreground/80">Hawk</span>
        <span>Onboarding</span>
      </div>

      <div className="relative w-full max-w-lg animate-hawk-rise space-y-8">
        <header className="text-center space-y-3">
          <h1 className="text-3xl font-medium tracking-[-0.02em]">
            Welcome{user?.name ? `, ${user.name}` : ''}
            <span className="text-primary">.</span>
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Signed in as{' '}
            <span className="font-mono text-foreground">{user?.email}</span>
            {isPlatformAdmin && (
              <>
                {' '}
                with the{' '}
                <span className="font-mono text-primary">platform_admin</span>{' '}
                role.
              </>
            )}
          </p>
        </header>

        {isPlatformAdmin ? <PlatformAdminFlow /> : <InviteeFlow />}
      </div>
    </PageMain>
  )
}

// ---------------------------------------------------------------------------
// Regular user: pending invites + paste field
// ---------------------------------------------------------------------------

function InviteeFlow() {
  const qc = useQueryClient()
  const [token, setToken] = useState('')
  const [pasteError, setPasteError] = useState<string | null>(null)

  const accept = useMutation({
    mutationFn: async (rawToken: string) => {
      const trimmed = rawToken.trim()
      if (!trimmed) throw new Error('Paste the invite token from your email.')
      const r = await apiFetch(
        `/api/invites/${encodeURIComponent(trimmed)}/accept`,
        { method: 'POST' },
      )
      if (!r.ok) throw new ApiError(r.status, await r.text())
    },
    onSuccess: () => {
      // Trigger the session refetch; the OnboardingPage useEffect will
      // navigate to /dashboard once the new currentOrgSlug propagates.
      qc.invalidateQueries({ queryKey: ['auth', 'session'] })
    },
    onError: (e: Error) =>
      setPasteError(e instanceof ApiError ? friendlyAcceptError(e) : e.message),
  })

  return (
    <section className="border border-border bg-card/80 backdrop-blur rounded-md p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Mail className="size-4 text-primary" strokeWidth={1.5} />
        <h2 className="text-sm font-medium tracking-tight">
          Accept your invitation
        </h2>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        Paste the invitation token from your email. It starts with{' '}
        <code className="font-mono">hawk_invite_</code>. If you didn't receive
        one, ask an organization admin to send one to your email address.
      </p>

      {pasteError && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30">
          <AlertCircle
            className="size-4 text-destructive shrink-0 mt-0.5"
            strokeWidth={1.5}
          />
          <p className="text-xs text-destructive flex-1 leading-relaxed">
            {pasteError}
          </p>
        </div>
      )}

      <Input
        value={token}
        onChange={(e) => {
          setToken(e.target.value)
          setPasteError(null)
        }}
        placeholder="hawk_invite_..."
        className="font-mono text-xs"
        spellCheck={false}
        autoComplete="off"
      />

      <Button
        onClick={() => accept.mutate(token)}
        disabled={accept.isPending || !token.trim()}
        className="w-full h-10"
      >
        {accept.isPending ? (
          <>
            <Loader2 className="size-4 animate-spin mr-2" />
            Accepting
          </>
        ) : (
          'Accept invitation'
        )}
      </Button>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Platform admin: create new org and/or pick an existing one
// ---------------------------------------------------------------------------

function PlatformAdminFlow() {
  const orgs = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => apiJson<Organization[]>('/api/admin/organizations'),
  })

  if (orgs.isLoading) {
    return (
      <section className="border border-border bg-card/80 backdrop-blur rounded-md p-6 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        <span className="font-mono uppercase tracking-[0.22em]">Loading</span>
      </section>
    )
  }

  const noOrgsYet = (orgs.data?.length ?? 0) === 0

  return noOrgsYet ? (
    <FirstOrgForm />
  ) : (
    <OrgPicker orgs={orgs.data!} />
  )
}

/// First-run experience: no orgs exist anywhere. Platform admin creates the
/// first one and is added as its admin.
function FirstOrgForm() {
  const qc = useQueryClient()
  const [slug, setSlug] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      setError(null)
      if (!isValidSlug(slug.trim())) {
        throw new Error(
          'Slug must be 1-64 characters, lowercase letters, digits, and hyphens.',
        )
      }
      const r = await apiFetch('/api/admin/organizations', {
        method: 'POST',
        body: JSON.stringify({
          slug: slug.trim(),
          display_name: displayName.trim() || null,
        }),
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'session'] })
      qc.invalidateQueries({ queryKey: ['admin', 'organizations'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <section className="border border-border bg-card/80 backdrop-blur rounded-md p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="size-4 text-primary" strokeWidth={1.5} />
        <h2 className="text-sm font-medium tracking-tight">
          Create the first organization
        </h2>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        There are no organizations yet. As platform admin, create the first one
        to bootstrap Hawk. You'll be added as its admin.
      </p>

      <div className="space-y-3">
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Slug
          </label>
          <Input
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value.toLowerCase())
              setError(null)
            }}
            placeholder="acme"
            className="font-mono text-sm"
            autoComplete="off"
            spellCheck={false}
          />
          <p className="text-[10px] font-mono text-muted-foreground/70">
            Lowercase letters, digits, hyphens. Used in URLs and PATs.
          </p>
        </div>

        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Display name <span className="text-muted-foreground/60">(optional)</span>
          </label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Acme Corp"
            autoComplete="off"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30">
          <AlertCircle
            className="size-4 text-destructive shrink-0 mt-0.5"
            strokeWidth={1.5}
          />
          <p className="text-xs text-destructive flex-1 leading-relaxed">
            {error}
          </p>
        </div>
      )}

      <Button
        onClick={() => create.mutate()}
        disabled={create.isPending || !slug.trim()}
        className="w-full h-10"
      >
        {create.isPending ? (
          <>
            <Loader2 className="size-4 animate-spin mr-2" />
            Creating
          </>
        ) : (
          <>
            <Plus className="size-4 mr-2" />
            Create organization
          </>
        )}
      </Button>
    </section>
  )
}

/// Picker: at least one org exists. Platform admin sets their active context
/// by choosing one. They are NOT added as a member - they can switch to any
/// org at any time via the sidebar dropdown.
function OrgPicker({ orgs }: { orgs: Organization[] }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const pick = useMutation({
    mutationFn: async (orgSlug: string) => {
      const r = await apiFetch('/auth/switch-org', {
        method: 'POST',
        body: JSON.stringify({ slug: orgSlug }),
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'session'] })
      navigate({ to: '/dashboard' })
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <section className="border border-border bg-card/80 backdrop-blur rounded-md p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Building2 className="size-4 text-primary" strokeWidth={1.5} />
        <h2 className="text-sm font-medium tracking-tight">
          Choose an organization to start in
        </h2>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        Platform admins are not tied to any organization. You can switch
        between organizations at any time from the sidebar.
      </p>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30">
          <AlertCircle
            className="size-4 text-destructive shrink-0 mt-0.5"
            strokeWidth={1.5}
          />
          <p className="text-xs text-destructive flex-1 leading-relaxed">
            {error}
          </p>
        </div>
      )}

      <ul className="space-y-1.5">
        {orgs.map((o) => (
          <li key={o.id}>
            <button
              type="button"
              onClick={() => pick.mutate(o.name)}
              disabled={pick.isPending}
              className="w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-secondary/40 border border-border hover:border-primary/40 hover:bg-secondary transition-colors disabled:opacity-50"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {o.display_name ?? o.name}
                </div>
                <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                  {o.name}
                </div>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Enter
              </span>
            </button>
          </li>
        ))}
      </ul>

      <div className="pt-2 border-t border-border text-center">
        <button
          type="button"
          onClick={() => navigate({ to: '/admin/organizations' })}
          className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground hover:text-primary transition-colors"
        >
          + Create a new organization
        </button>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidSlug(s: string): boolean {
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(s) && s.length <= 64
}

function friendlyAcceptError(err: ApiError): string {
  if (err.status === 403) {
    if (err.body.includes('expired')) return 'This invite has expired.'
    if (err.body.includes('already accepted'))
      return 'This invite has already been accepted.'
    if (err.body.includes('different account'))
      return 'This invite was issued for a different email address.'
    return 'You are not allowed to accept this invite.'
  }
  if (err.status === 404) return 'Invite not found. Check the token and try again.'
  if (err.status === 400) return 'Token is malformed. Make sure you copied the whole value.'
  return `Server error (${err.status}). Try again.`
}
